<template>
  <div class="stat-roller stack tight">
    <div class="head">
      <div>
        <div class="title">Attributes</div>
        <div class="text-muted subtitle">Roll or tune your starting stats.</div>
      </div>
      <UiButton size="sm" variant="secondary" @click="roll">
        <template #icon>{{ ICONS.actions.dice }}</template>
        Roll 4d6 drop lowest
      </UiButton>
    </div>

    <div class="grid">
      <label v-for="(value, key) in localAttrs" :key="key" class="stat">
        <span class="stat-label">{{ String(key).toUpperCase() }}</span>
        <input
          class="ui-input stat-mono num"
          type="number"
          inputmode="numeric"
          min="3"
          max="20"
          step="1"
          :value="String(value)"
          @change="(e) => onInput(key, e)"
        />
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watchEffect } from "vue";
import type { Attributes } from "@idle-rpg/shared";
import { ICONS } from "../lib/icons";
import UiButton from "./ui/UiButton.vue";

const model = defineModel<Attributes>({ required: true });

const localAttrs = reactive<Attributes>({
  str: 10,
  dex: 10,
  con: 10,
  int: 10,
  wis: 10,
  cha: 10
});

function clampAttr(value: number): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return 10;
  return Math.max(3, Math.min(20, n));
}

function onInput(key: keyof Attributes, event: Event) {
  const el = event.target as HTMLInputElement;
  localAttrs[key] = clampAttr(Number(el.value));
}

function rollStat(): number {
  const rolls = Array.from({ length: 4 }, () => Math.floor(Math.random() * 6) + 1);
  rolls.sort((a, b) => a - b);
  return rolls.slice(1).reduce((sum, n) => sum + n, 0);
}

function roll() {
  localAttrs.str = rollStat();
  localAttrs.dex = rollStat();
  localAttrs.con = rollStat();
  localAttrs.int = rollStat();
  localAttrs.wis = rollStat();
  localAttrs.cha = rollStat();
}

watchEffect(() => {
  model.value = { ...localAttrs };
});

roll();
</script>

<style scoped>
.stat-roller {
  min-width: 0;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
}

.title {
  font-weight: 900;
  letter-spacing: 0.02em;
}

.subtitle {
  margin-top: 0.15rem;
  font-size: 0.9rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}

.stat {
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  border-radius: var(--radius-2);
  padding: 0.65rem 0.7rem;
  display: grid;
  gap: 0.4rem;
}

.stat-label {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.ui-input {
  width: 100%;
  padding: 0.45rem 0.55rem;
  text-align: center;
}

@media (max-width: 520px) {
  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
